import AccessRequestTableMine from 'components/AccessRequests/Tables/Mine';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getColumns } from './columns';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  createDashboardQuery,
  deleteDashboardQuery,
  duplicateDashboardQuery,
  fetchDashboardsQuery,
} from 'components/AccessRequests/useDashboardList';
import useModal from 'components/Modals/useModal';
import CreateLayoutModal from 'components/DashboardGrid/CreateLayoutModal';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import Loader from 'components/Kizen/Loader';
import useDashboardConfig from 'pages/Dashboard/hooks/useDashboardConfig';
import { useHomepageOptimisticUpdate } from 'pages/Homepages/hooks/useHomepageOptimisticUpdate';
import { getDashboardsQueryKeyForHomepages } from 'pages/Homepages/utils';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { useReorderDashboards } from 'components/DashboardGrid/hooks/useReorderDashboards';
import searchState from 'components/AccessRequests/searchState';
import SearchState from 'components/AccessRequests/searchState';
import {
  DashboardStyleSettingsProvider,
  DEFAULT_DASHLET_STYLE_SETTINGS,
} from 'ts-components/StyleSettings';

const INCLUDE_SHARING = true;

const HomepagesSettingsPageContent = () => {
  const [existingDashboard, setExistingDashboard] = useState({});
  const [isDuplication, setIsDuplication] = useState(false);
  const [backToDashboardId, setBackToDashboardId] = useState(null);

  const { t } = useTranslation();
  const { debouncedTerm } = useContext(searchState.Context);

  useSetTitleOnLoad(t('Homepage Settings'));

  const handleFetchDashboards = useCallback(() => {
    return fetchDashboardsQuery(
      debouncedTerm,
      INCLUDE_SHARING,
      undefined,
      'homepage'
    );
  }, [debouncedTerm]);

  const dashboardsQueryKey = getDashboardsQueryKeyForHomepages({
    debouncedTerm,
    includeSharing: INCLUDE_SHARING,
  });

  const {
    isLoading: myDashboardsLoading,
    data: homepages = [],
    refetch: refetchDashboards,
    isRefetching: isRefetchingDashboards,
  } = useQuery({
    queryKey: dashboardsQueryKey,
    queryFn: handleFetchDashboards,
    placeholderData: keepPreviousData,
  });

  const handleReorderDashboards = useReorderDashboards({
    onSuccess: refetchDashboards,
    queryKey: dashboardsQueryKey,
  });

  const {
    afterUpdate,
    afterCreate,
    afterDelete,
    loading: configLoading,
  } = useDashboardConfig(homepages, true, 'homepage');
  const [errors, setErrors] = useState({});

  const createDashboardMutation = useMutation({
    mutationFn: (result) => createDashboardQuery(result, undefined, 'homepage'),
    onMutate: () => {
      setErrors({});
    },
    onSuccess: (data) => {
      handleHideModal();
      afterCreate(data.id);
      setBackToDashboardId(data.id);
      setExistingDashboard({});
      setIsDuplication(false);
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors);
    },
  });

  const deleteDashboardMutation = useMutation({
    mutationFn: (result) => deleteDashboardQuery(result),
    onSuccess: (_data, id) => {
      afterDelete(id);
      setBackToDashboardId(null);
      refetchDashboards();
    },
  });

  const duplicateDashboardMutation = useMutation({
    mutationFn: (result) =>
      duplicateDashboardQuery(result.id, result.isNameChanged, result.payload),
    onSuccess: (data) => {
      handleHideModal();
      setBackToDashboardId(data.id);
      afterCreate(data.id);
      setExistingDashboard({});
      setIsDuplication(false);
      refetchDashboards();
    },
  });

  const updateDashboardMutation = useHomepageOptimisticUpdate({
    afterUpdate,
    refetchDashboards,
    includeSharing: INCLUDE_SHARING,
    debouncedTerm,
    onSuccess: (_, { isFromModal }) => {
      if (isFromModal) {
        handleHideModal();
      }
      setExistingDashboard({});
      setIsDuplication(false);
    },
    onError: (errs, { id, isFromModal, ...rest }) => {
      setErrors((...prev) => {
        return {
          ...prev,
          ...errs,
          errorForId: isFromModal ? undefined : id,
        };
      });
    },
  });

  const handleMutateDashboard = useCallback(
    (
      { name, api_name, permissions, owner = {}, published, styleSettings },
      isEditing,
      isNameChanged,
      id,
      isDuplication,
      isFromModal
    ) => {
      if (isDuplication) {
        duplicateDashboardMutation.mutate({
          id,
          isNameChanged,
          payload: {
            name,
            ...buildSharingSettingsForAPI(permissions),
            style_settings: styleSettings,
            ...owner,
          },
        });
      } else if (isEditing) {
        const payload = {
          name,
          api_name,
          ...buildSharingSettingsForAPI(permissions),
          style_settings: styleSettings,
          ...owner,
        };

        if (typeof published !== 'undefined') {
          payload.published = published;
        }

        updateDashboardMutation.mutate({
          id,
          payload,
          isFromModal,
        });
      } else {
        createDashboardMutation.mutate({
          name,
          style_settings: styleSettings,
          ...buildSharingSettingsForAPI(permissions),
        });
      }
    },
    [
      createDashboardMutation,
      updateDashboardMutation,
      duplicateDashboardMutation,
    ]
  );

  const [modalProps, , { show: handleShowModal, hide: handleHideModal }] =
    useModal({
      closeOnSubmit: false,
      handleSubmit: (data, isEditing, isNameChanged, id, isDuplication) =>
        handleMutateDashboard(
          data,
          isEditing,
          isNameChanged,
          id,
          isDuplication,
          true
        ),
      handleHide: () => {
        setErrors({});
        setExistingDashboard({});
        // setCanUserEditPermissions(true);
      },
    });

  if (myDashboardsLoading) {
    return <Loader loading />;
  }

  return (
    <>
      <AccessRequestTableMine
        getColumns={getColumns}
        entityPlural={t('Homepages')}
        entitySingular={t('Homepage')}
        data={homepages}
        prefix=""
        goBackPath={backToDashboardId ? `/home/${backToDashboardId}` : '/home'}
        handleMutateDashboard={handleMutateDashboard}
        handleShowModal={(...args) => {
          setErrors({});
          handleShowModal(...args);
        }}
        handleStartEditing={(d) => {
          setExistingDashboard(d);
          setIsDuplication(false);
          setErrors({});
          handleShowModal();
        }}
        handleStartDuplication={(d) => {
          setExistingDashboard(d);
          setIsDuplication(true);
          setErrors({});
          handleShowModal();
        }}
        loading={configLoading || myDashboardsLoading}
        handleReorderDashboards={handleReorderDashboards}
        updateDashboardMutation={updateDashboardMutation}
        deleteDashboardMutation={deleteDashboardMutation}
        isRefetching={isRefetchingDashboards}
        deleteConfirmationMessage={t(
          'Deleting this homepage will remove it for all team members, including homepage administrators. This action is permanent and the homepage cannot be recovered.'
        )}
        errors={errors}
        setErrors={setErrors}
        canEdit
        canDelete
      />
      <DashboardStyleSettingsProvider
        styleSettings={DEFAULT_DASHLET_STYLE_SETTINGS}
      >
        <CreateLayoutModal
          {...modalProps}
          isDuplication={isDuplication}
          canUserEditPermissions
          existing={existingDashboard}
          entity={t('Homepage')}
          errors={errors}
          blockHideIfDirty
          showPrivateToggle={false}
          forceAdmin
        />
      </DashboardStyleSettingsProvider>
    </>
  );
};

const HomepageSettingsPage = () => {
  return (
    <SearchState.Wrapper>
      <HomepagesSettingsPageContent />
    </SearchState.Wrapper>
  );
};

export default HomepageSettingsPage;
